/**
 * PLANET MANAGEMENT PANEL — balance constants.
 *
 * All gameplay values for Outposts, mining stations, relays, scanners,
 * upkeep, scanning resolution, mining rates, and profile randomization
 * live here. This is the only place to tune planet economy.
 *
 * NOTE: numbers here are placeholders for v1 — tuning happens after the
 * panel is in players' hands.
 */

export const PLANET_BALANCE = {
  // ==================== STRUCTURE COSTS ====================
  OUTPOST_COST: 25000,
  MINING_STATION_COST: 2000,
  RELAY_COST: 10000,
  SCANNER_COST: 15000,

  // ==================== DISMANTLE ====================
  /** Fraction of cost recovered on voluntary dismantle (50% per spec). */
  DISMANTLE_RECOVERY_PERCENT: 0.5,
  /** Cooldown between place→dismantle of the same structure (anti-grief). */
  DISMANTLE_COOLDOWN_MS: 60 * 60 * 1000, // 1 hour

  // ==================== UPKEEP ====================
  /** Daily upkeep cost as fraction of build cost. */
  UPKEEP_DAILY_PERCENT: 0.02, // 2%/day
  /** Days of unpaid upkeep before auto-dismantle (full loss). */
  UPKEEP_GRACE_DAYS: 7,
  /** Background check interval (server). */
  UPKEEP_CHECK_INTERVAL_MS: 60 * 60 * 1000, // hourly

  // ==================== HEX COVERAGE (Phase 4+) ====================
  // Authoritative coverage of a structure = central hex + N rings of neighbors.
  // On a G(8) hexsphere (642 cells):
  //   0 rings → 1 hex   (~0.16% of surface)
  //   1 ring  → 7 hexes (~1.1%)
  //   2 rings → 19 hexes (~3.0%)
  //   3 rings → 37 hexes (~5.8%)
  OUTPOST_RINGS: 2,
  MINING_STATION_RINGS: 1,
  RELAY_RINGS: 2,
  SCANNER_RINGS: 3,

  /**
   * Minimum graph distance (hex hops) between two of the SAME player's
   * outposts. With 2-ring coverage that's 5 hexes diameter, so a 3-hop
   * minimum leaves at least one un-covered ring of separation between
   * adjacent personal outposts — anti-stacking without forbidding two
   * outposts on the same continent.
   */
  OWN_OUTPOST_MIN_HEX_DISTANCE: 3,

  /**
   * Approximate angular radius (radians) per ring on the G(8) hexsphere.
   * Empirical: the average hex-to-hex centroid step on G(8) is ~0.105 rad.
   * Used to derive radius_km for legacy great-circle rendering and reach
   * checks where graph distance is overkill (e.g. UI proximity prompts).
   */
  HEX_RING_ANGULAR_RAD: 0.105,

  // ==================== LEGACY RADII (deprecated) ====================
  // Kept for code paths that still expect km-radius (rendering fallbacks,
  // panel-open proximity prompt). Phase 7 removes these once all callers
  // migrate to the hex coverage model.
  OUTPOST_RADIUS_FRACTION: 0.003,
  MINING_STATION_RADIUS_KM: 300,
  RELAY_RADIUS_FRACTION: 0.002,
  SCANNER_RADIUS_FRACTION: 0.005,
  OWN_OUTPOST_MIN_DISTANCE_FRACTION: 0.0015,
  /** Legacy "minimum distance between any two structures". Hex model uses
   *  "no two structures in the same hex" — this constant is unused going
   *  forward but kept for one phase to avoid breaking compile-time refs. */
  MIN_STRUCTURE_DISTANCE_KM: 3,

  // ==================== BUFFER ====================
  /** Max total units (across all resource types) an Outpost can hold. */
  OUTPOST_BUFFER_CAPACITY: 10000,

  // ==================== MINING RATES ====================
  // Units per minute extracted from the field. Tier of extracted resource
  // is chosen at planting time from the local field sample.
  MINING_RATE_T1: 2.0,
  MINING_RATE_T2: 1.5,
  MINING_RATE_T3: 1.0,
  MINING_RATE_T4: 0.5,
  /** How often the mining tick runs (server). */
  MINING_TICK_INTERVAL_MS: 5 * 60 * 1000, // 5min — units/min calc keeps hourly yield identical
  /** Number of ring points around a mining station's centre for area sampling. */
  MINING_STATION_SAMPLE_RING: 6,
  /**
   * Each richness point from the noise field represents this many
   * extractable resource units. Without this, richness ~1.0 is consumed
   * in a single 30 s tick. With 50, a cell at richness 1.0 holds 50
   * extractable units → ~25 min to deplete at T1 rate.
   */
  RICHNESS_POOL_SCALE: 1000,

  // ==================== FIELD GENERATION ====================
  /** Cell size for the depletion grid. ~1km² per cell on Earth-sized planet. */
  RICHNESS_FIELD_RESOLUTION_KM: 1,

  // ==================== MIGRATION (Phase 5) ====================
  //
  // The planet's geology drifts: heavily-depleted hexes "leak" some of
  // their lost richness into a distant hex (weighted by affinity), and
  // all hexes slowly decay back toward baseline. Result: rich clusters
  // mined to the floor reappear somewhere else, breaking first-mover lock.

  /** A hex starts migrating when its negative delta exceeds this fraction
   *  of its baseline. 0.5 = ≥50% depleted. */
  MIGRATION_THRESHOLD_FRACTION: 0.5,
  /** Fraction of baseline transferred per day from a heavily-depleted hex
   *  to a randomly chosen destination. 0.20 = 20% / day. */
  MIGRATION_RATE_PER_DAY: 0.20,
  /** Passive decay toward baseline (per day). Applies to ALL drifted hexes
   *  — keeps long-tail enrichment from accumulating forever. 0.02 = 2% / day. */
  DECAY_PER_DAY: 0.02,
  /** Below this absolute |delta| threshold a hex is considered clean again
   *  and its row is removed from planet_hex_state. */
  HEX_CLEANUP_EPSILON: 0.001,
  /** Migration destinations must be at least this many graph hops away
   *  from the source hex. Prevents resources from just leaking to an
   *  immediate neighbor (which would let a player chase with a relay). */
  MIGRATION_MIN_HEX_DISTANCE: 4,

  // ==================== ORBITAL SCAN ====================
  // Always available when the panel opens (no scanner needed).
  ORBITAL_SCAN_NOISE: 0.3,            // ±30% noise
  ORBITAL_SCAN_RESOLUTION_KM: 100,    // very coarse

  // ==================== SCANNER ARRAY ====================
  // Inside a scanner's radius, the heatmap upgrades to high-resolution
  // low-noise data.
  SCANNER_NOISE: 0.05,                // ±5% noise
  SCANNER_RESOLUTION_KM: 1,
  /** Max scanner arrays per Outpost (keeps the scanner cost meaningful). */
  MAX_SCANNERS_PER_OUTPOST: 1,

  // ==================== PROFILE RANDOMIZATION ====================
  PROFILE_SIZE_MIN: 0.5,
  PROFILE_SIZE_MAX: 2.0,
  PROFILE_RICHNESS_MIN: 0.3,
  PROFILE_RICHNESS_MAX: 2.5,
  PROFILE_CLUSTER_MIN: 0.2,
  PROFILE_CLUSTER_MAX: 1.0,

  // ==================== ACTIVITY LOG ====================
  /** Max entries persisted per planet (older entries trimmed periodically). */
  ACTIVITY_LOG_MAX_ENTRIES_PER_PLANET: 100,
  /** Entries shown in the panel's recent-activity strip. */
  ACTIVITY_LOG_DISPLAY_COUNT: 10,

  // ==================== PANEL VIEWER REGISTRY ====================
  /** Soft cap on concurrent viewers per planet (broadcast performance). */
  MAX_VIEWERS_PER_PLANET: 50,

  // ==================== PROXIMITY ====================
  /** Multiplier of planet radius defining "near enough to open panel". */
  PANEL_OPEN_RANGE_MULTIPLIER: 2.0,
} as const;

// ==================== HELPERS ====================

/** Resolve mining rate (units/min @ 100% richness) for a given tier. */
export function miningRateForTier(tier: 1 | 2 | 3 | 4): number {
  switch (tier) {
    case 1: return PLANET_BALANCE.MINING_RATE_T1;
    case 2: return PLANET_BALANCE.MINING_RATE_T2;
    case 3: return PLANET_BALANCE.MINING_RATE_T3;
    case 4: return PLANET_BALANCE.MINING_RATE_T4;
  }
}

/** Daily upkeep cost (credits) for a structure of the given build cost. */
export function dailyUpkeepCost(buildCost: number): number {
  return Math.ceil(buildCost * PLANET_BALANCE.UPKEEP_DAILY_PERCENT);
}

/** Credits returned by a voluntary dismantle. */
export function dismantleRefund(buildCost: number): number {
  return Math.floor(buildCost * PLANET_BALANCE.DISMANTLE_RECOVERY_PERCENT);
}

/** Build cost lookup by structure type. */
export function buildCostForStructure(
  type: 'outpost' | 'mining_station' | 'relay' | 'scanner'
): number {
  switch (type) {
    case 'outpost':         return PLANET_BALANCE.OUTPOST_COST;
    case 'mining_station':  return PLANET_BALANCE.MINING_STATION_COST;
    case 'relay':           return PLANET_BALANCE.RELAY_COST;
    case 'scanner':         return PLANET_BALANCE.SCANNER_COST;
  }
}

/** Hex ring count covered by a structure type. */
export function ringsForStructure(
  type: 'outpost' | 'mining_station' | 'relay' | 'scanner'
): number {
  switch (type) {
    case 'outpost':         return PLANET_BALANCE.OUTPOST_RINGS;
    case 'mining_station':  return PLANET_BALANCE.MINING_STATION_RINGS;
    case 'relay':           return PLANET_BALANCE.RELAY_RINGS;
    case 'scanner':         return PLANET_BALANCE.SCANNER_RINGS;
  }
}

/**
 * Derive a legacy great-circle radius (km) from a structure's hex ring
 * coverage. Approximates the angular radius as `rings * HEX_RING_ANGULAR_RAD`
 * and multiplies by the planet's radius. Use for rendering / proximity
 * fallbacks; the authoritative coverage check goes through the hex graph.
 */
export function ringsRadiusKm(rings: number, planetRadiusKm: number): number {
  return rings * PLANET_BALANCE.HEX_RING_ANGULAR_RAD * planetRadiusKm;
}

// ==================== EARTH-SIZED PLANET CONSTANTS ====================
// All "fraction of surface" radii are converted to km using a baseline
// Earth-sized planet (R ≈ 6371 km → surface ≈ 5.1e8 km²) scaled by the
// planet's profile size_multiplier. Kept here so server + client agree.

export const EARTH_RADIUS_KM = 6371;
export const EARTH_SURFACE_AREA_KM2 = 4 * Math.PI * EARTH_RADIUS_KM * EARTH_RADIUS_KM;

/** Convert a "fraction of surface" radius into a great-circle radius (km). */
export function fractionToRadiusKm(
  surfaceFraction: number,
  sizeMultiplier: number
): number {
  const planetRadius = EARTH_RADIUS_KM * Math.sqrt(sizeMultiplier);
  const planetSurface = 4 * Math.PI * planetRadius * planetRadius;
  const circleArea = planetSurface * surfaceFraction;
  // circle area = π r²  →  r = sqrt(area/π)
  return Math.sqrt(circleArea / Math.PI);
}
